import React from 'react';

interface StarRatingProps {
    rating: number;
    interactive?: boolean;
    onRatingChange?: (rating: number) => void;
    size?: 'small' | 'medium' | 'large';
    showValue?: boolean;
    className?: string;
}

const StarRating: React.FC<StarRatingProps> = ({
    rating,
    interactive = false,
    onRatingChange,
    size = 'medium',
    showValue = false,
    className = '',
}) => {
    const [hoverRating, setHoverRating] = React.useState<number | null>(null);

    // Helper function to determine which star class to use
    const getStarClass = (position: number, currentRating: number) => {
        if (position <= Math.floor(currentRating)) {
            return 'fa-star'; // Full star
        } else if (position === Math.ceil(currentRating) && !Number.isInteger(currentRating)) {
            return 'fa-star-half-o'; // Half star
        } else {
            return 'fa-star-o'; // Empty star
        }
    };

    // Determine size class
    const sizeClass = {
        'small': 'star-rating-sm',
        'medium': 'star-rating-md',
        'large': 'star-rating-lg',
    }[size];

    const handleMouseMove = (e: React.MouseEvent<HTMLElement>, starIndex: number) => {
        if (!interactive) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const halfStar = mouseX < rect.width / 2;
        setHoverRating(halfStar ? starIndex - 0.5 : starIndex);
    };

    const handleMouseLeave = () => {
        if (!interactive) return;
        setHoverRating(null);
    };

    const handleStarClick = (e: React.MouseEvent<HTMLElement>, starIndex: number) => {
        if (!interactive || !onRatingChange) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const newRating = mouseX < rect.width / 2 ? starIndex - 0.5 : starIndex;
        onRatingChange(newRating);
    };

    // Determine the effective rating (either hover rating or the actual rating)
    const effectiveRating = hoverRating !== null ? hoverRating : rating;

    return (
        <div className={`star-rating ${sizeClass} ${className}`}>
            {showValue && (
                <div className="rating-value">
                    <strong>{rating.toFixed(1)}</strong>
                </div>
            )}
            <div className="stars-container" onMouseLeave={handleMouseLeave}>
                {[1, 2, 3, 4, 5].map((i) => (
                    <div
                        key={`star-${i}`}
                        className="star-wrapper"
                        style={{
                            display: 'inline-block',
                            position: 'relative',
                            cursor: interactive ? 'pointer' : 'default',
                        }}
                        onMouseMove={(e) => handleMouseMove(e, i)}
                        onClick={(e) => handleStarClick(e, i)}
                    >
                        <i
                            className={`fa ${getStarClass(i, effectiveRating)}`}
                            aria-hidden="true"
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

export default StarRating; 